'use client';

import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslations } from 'next-intl';
import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CelebrityShareModal } from '@/components/molecules/ActionCard/CelebrityShareModal';
import { CelebrityShareSuccessModal } from '@/components/molecules/ActionCard/CelebrityShareSuccessModal';
import { shareCelebrity } from '@/entities/Actions/api/celebrityShare';
import type { Celebrity } from '@/entities/Actions/type/action';
import { notification } from '@/utils/antd-imports';
interface CelebrityShareData {
  shareUrl: string;
  reward: {
    poe: number;
    coins: number;
  };
  isFirstTime: boolean;
}
interface CelebrityShareContextType {
  openCelebrityShare: (celebrityId: number, celebrity?: Celebrity | null) => Promise<void>;
  isShareModalOpen: boolean;
  isRewardModalOpen: boolean;
  closeShareModal: () => void;
  closeRewardModal: () => void;
}
const CelebrityShareContext = createContext<CelebrityShareContextType | null>(null);
export const useCelebrityShare = () => {
  const context = useContext(CelebrityShareContext);
  if (!context) {
    throw new Error("useCelebrityShare must be used within CelebrityShareProvider");
  }
  return context;
};
export const CelebrityShareProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const tDetail = useTranslations('experiences.detail');
  const queryClient = useQueryClient();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [shareData, setShareData] = useState<CelebrityShareData | null>(null);
  const [celebrity, setCelebrity] = useState<Celebrity | null>(null);
  const [rewardModalData, setRewardModalData] = useState<{
    poe: number;
    coins: number;
  } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [api, contextHolder] = notification.useNotification();
  const closeShareModal = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsShareModalOpen(false);
    setShareData(null);
    setCelebrity(null);
  }, []);
  const closeRewardModal = useCallback(() => {
    setIsRewardModalOpen(false);
    setRewardModalData(null);
    queryClient.invalidateQueries({
      queryKey: ['user-balance']
    });
  }, [queryClient]);
  const openCelebrityShare = useCallback(async (celebrityId: number, celebrityData?: Celebrity | null) => {
    try {
      const response = await shareCelebrity(celebrityId);
      setShareData({
        shareUrl: response.shareUrl,
        reward: response.reward,
        isFirstTime: response.isFirstTime
      });
      setCelebrity(celebrityData ?? null);
      setIsShareModalOpen(true);
      if (response.isFirstTime && (response.reward.poe > 0 || response.reward.coins > 0)) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          setIsShareModalOpen(false);
          setShareData(null);
          setCelebrity(null);
          setRewardModalData(response.reward);
          setIsRewardModalOpen(true);
        }, 10000);
      }
    } catch (error) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      const message = error instanceof AxiosError ? error.response?.status === 400 ? 'Card not available' : error.response?.status === 401 ? 'Unauthorized' : error.response?.status === 404 ? 'Celebrity not found' : undefined : undefined;
      api.error({
        message: message ?? tDetail('shareExperience.error'),
        placement: 'bottom'
      });
    }
  }, [api, tDetail]);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  return <CelebrityShareContext.Provider value={{
    openCelebrityShare,
    isShareModalOpen,
    isRewardModalOpen,
    closeShareModal,
    closeRewardModal
  }}>
      {contextHolder}
      {children}
      <CelebrityShareModal isOpen={isShareModalOpen} onClose={closeShareModal} celebrity={celebrity ?? undefined} shareUrl={shareData?.shareUrl ?? ''} />
      {rewardModalData && <CelebrityShareSuccessModal isOpen={isRewardModalOpen} onClose={closeRewardModal} reward={rewardModalData} />}
    </CelebrityShareContext.Provider>;
};